import * as React from 'react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import dateUtil from '../../../utils/dateUtil';
import ShiftService from '../services/ShiftService';

import 'react-datepicker/dist/react-datepicker.css';

import TimeTrackings from '../../timetracking/components/TimeTrackings';
import Absences from '../../absences/components/Absences';
import Availabilities from '../../availabilities/components/Availabilities';
import EmployeeSidebar from '../../../EmployeeSidebar';
import ShiftTable from './ShiftTable';
import ShiftDashboard from './ShiftDashboard';
import AddShiftModal from './AddShiftModal';
import EditShiftModal from './EditShiftModal';
import ClearAllModal from './ClearAllModal';
import AutoAssignOverlay from './AutoAssignOverlay';
import { WeeklyShiftsAbsenceModal } from './WeeklyShiftsAbsenceModal';

import { useWeeklyShifts } from './useWeeklyShifts';
import { WeeklyShiftsHeader } from './WeeklyShiftsHeader';
import {
  LocationFilterWrapper,
  WAFilterDNDWrapper,
  TTWAFilterRow,
  AbsenceWAFilterRow,
} from './WeeklyShiftsFilters';
import {
  formatHeader,
  getWeatherIcon,
  addOneHour,
} from './weeklyShiftsUtils';
import styles from './WeeklyShifts.module.css';

function WeeklyShifts() {
  const h = useWeeklyShifts();

  const renderLocationFilter = () => (
    <LocationFilterWrapper
      uniqueLocations={h.uniqueLocations}
      selectedLocations={h.selectedLocations}
      setSelectedLocations={h.setSelectedLocations}
    />
  );

  const renderWAFilterDND = () => (
    <WAFilterDNDWrapper
      draggableWAList={h.draggableWAList}
      hiddenWorkAreas={h.hiddenWorkAreas}
      setHiddenWorkAreas={h.setHiddenWorkAreas}
      handleWADragEnd={h.handleWADragEnd}
      selectedLocations={h.selectedLocations}
      isManager={h.isManager}
      workAreaIds={h.workAreaIds}
    />
  );

  const renderTTWAFilter = () => (
    <TTWAFilterRow
      workAreaOptions={h.workAreaOptions}
      ttSelectedWAs={h.ttSelectedWAs}
      setTtSelectedWAs={h.setTtSelectedWAs}
      setTtExportOpen={h.setTtExportOpen}
      setTtImportOpen={h.setTtImportOpen}
      setTtAddOpen={h.setTtAddOpen}
    />
  );

  const renderAbsenceWAFilter = () => (
    <AbsenceWAFilterRow
      workAreaOptions={h.workAreaOptions}
      absenceSelectedWAs={h.absenceSelectedWAs}
      setAbsenceSelectedWAs={h.setAbsenceSelectedWAs}
      setAbsenceImportOpen={h.setAbsenceImportOpen}
      setAbsenceAddOpen={h.setAbsenceAddOpen}
    />
  );

  const handleDeleteWithUndo = async () => {
    const result = await h.handleEditShiftDelete();
    if (!result) return;
    const { deletedIds, fetchTableShifts } = result;
    const count = deletedIds.length;
    const label = count === 1 ? 'Shift deleted' : `${count} Shifts deleted`;
    const UNDO_DURATION = 6000;

    const undoPillId = 'undo-pill-styles';
    if (!document.getElementById(undoPillId)) {
      const s = document.createElement('style');
      s.id = undoPillId;
      s.textContent = `
        .undo-pill-toast.Toastify__toast {
          background: #f0f2f8 !important;
          border-radius: 50px !important;
          padding: 6px 10px 12px 20px !important;
          min-height: unset !important;
          box-shadow: 0 4px 20px rgba(14,19,51,0.15) !important;
          color: #0E1333 !important;
          overflow: hidden !important;
          margin-top: 8px;
          min-width: 340px;
          border: 1px solid rgba(14,19,51,0.08) !important;
          backdrop-filter: none !important;
        }
        .undo-pill-toast .Toastify__toast-body {
          padding: 0 !important;
          margin: 0 !important;
          flex: 1 !important;
          width: 100% !important;
        }
      `;
      document.head.appendChild(s);
    }

    toast(
      ({ closeToast }) => (
        <div style={{ width: '100%' }}>
          <div className={styles.undoRow}>
            <button
              className={styles.undoBtn}
              onClick={async () => {
                try {
                  await ShiftService.undoDelete(deletedIds);
                  closeToast?.();
                  await fetchTableShifts();
                  toast.success(`${count} Shift(s) wiederhergestellt`, { position: 'top-center' });
                } catch {
                  toast.error('Undo fehlgeschlagen', { position: 'top-center' });
                }
              }}
            >
              Undo
            </button>
            <span className={styles.undoLabel}>{label}</span>
            <button className={styles.undoClose} onClick={() => closeToast?.()}>
              &times;
            </button>
          </div>
          <div className={styles.undoProgressTrack}>
            <div
              className={styles.undoProgressBar}
              style={{ animation: `undoCountdown ${UNDO_DURATION}ms linear forwards` }}
            />
          </div>
          <style>{`@keyframes undoCountdown { from { width: 100%; } to { width: 0%; } }`}</style>
        </div>
      ),
      {
        autoClose: UNDO_DURATION,
        closeOnClick: false,
        closeButton: false,
        hideProgressBar: true,
        position: 'top-center',
        className: 'undo-pill-toast',
      }
    );
  };

  return (
    <div data-testid="ws-root" className={styles.root}>
      <WeeklyShiftsHeader
        activeTab={h.activeTab}
        setActiveTab={h.setActiveTab}
        startDate={h.startDate}
        setStartDate={h.setStartDate}
        calendarBtnRef={h.calendarBtnRef}
        setShowCalendar={h.setShowCalendar}
        setCalendarPos={h.setCalendarPos}
        handleAutoAssignClick={h.handleAutoAssignClick}
        handleClearAllClick={h.handleClearAllClick}
        showAddShift={h.showAddShift}
        setShowAddShift={h.setShowAddShift}
        setShiftData={h.setShiftData}
        lastShiftDefaults={h.lastShiftDefaults}
        workAreaOptions={h.workAreaOptions}
        renderLocationFilter={renderLocationFilter}
        renderWAFilterDND={renderWAFilterDND}
        renderTTWAFilter={renderTTWAFilter}
        renderAbsenceWAFilter={renderAbsenceWAFilter}
      />

      <div className={styles.contentArea}>
        {h.activeTab !== 'dashboard' && (
        <div data-testid="ws-sidebar" className={styles.sidebar}>
          {h.activeTab === 'shifts' && (
            <EmployeeSidebar
              employees={h.allEmployees}
              displayMode="shifts"
              activeEmployeeId={h.activeEmployeeId}
              activeEmployee={h.activeEmployee}
              setActiveEmployee={h.setActiveEmployee}
              setActiveEmployeeId={h.setActiveEmployeeId}
              selectedWorkAreas={h.draggableWAList
                .filter((wa: any) => !h.hiddenWorkAreas.includes(wa.id))
                .map((wa: any) => wa.id)}
              weeklyShifts={h.tableShifts}
            />
          )}
          {h.activeTab === 'timeTrackings' && (
            <EmployeeSidebar
              employees={h.allEmployees}
              displayMode="timeTracking"
              timeTrackings={h.displayedTTs}
              customStart={h.ttRangeStart instanceof Date ? h.ttRangeStart.toISOString().split('T')[0] : ''}
              customEnd={h.ttRangeEnd instanceof Date ? h.ttRangeEnd.toISOString().split('T')[0] : ''}
              timeRangeMode={h.ttRangeMode}
              activeEmployeeId={h.activeEmployeeId}
              setActiveEmployeeId={h.setActiveEmployeeId}
              plannedHoursMap={h.plannedHoursMap}
            />
          )}
          {h.activeTab === 'absences' && (
            <EmployeeSidebar
              employees={h.allEmployees}
              displayMode="absences"
              absences={h.allAbsencesForSidebar as any}
              contracts={h.employeeContracts as any}
              activeEmployeeId={h.activeEmployeeId}
              setActiveEmployeeId={h.setActiveEmployeeId}
              activeEmployee={h.activeEmployee}
              setActiveEmployee={h.setActiveEmployee}
            />
          )}
          {h.activeTab === 'availabilities' && (
            <EmployeeSidebar
              employees={h.allEmployees}
              displayMode="availability"
              activeEmployeeId={h.activeEmployeeId}
              setActiveEmployeeId={h.setActiveEmployeeId}
              activeEmployee={h.activeEmployee}
              setActiveEmployee={h.setActiveEmployee}
            />
          )}
        </div>
        )}

        <div className={styles.mainColumn}>
          <div className={styles.scrollableContent} data-testid="ws-scrollable-content">
            {h.activeTab === 'shifts' && (
              <div className={styles.shiftContentWrapper}>
                <ShiftTable
                  tableShifts={h.tableShifts}
                  startDate={h.startDate}
                  draggableWAList={h.draggableWAList}
                  allEmployees={h.allEmployees}
                  weekAbsences={h.weekAbsences}
                  weatherData={h.weatherData}
                  weatherByLocation={h.weatherByLocation}
                  uniqueLocations={h.uniqueLocations}
                  countsByDate={h.countsByDate}
                  activeEmployeeId={h.activeEmployeeId}
                  dragging={h.dragging}
                  dropTarget={h.dropTarget}
                  swapRequests={h.swapRequests}
                  hiddenWorkAreas={h.hiddenWorkAreas}
                  weekEvents={h.weekEvents}
                  onShiftDragEnd={h.handleShiftDragEnd}
                  onShiftClick={h.handleShiftClick}
                  onEditClick={h.handleEditClick}
                  onLongPressStart={h.handleLongPressStart}
                  onToggleAssignment={h.handleToggleAssignment}
                  onQuickAdd={h.handleQuickAdd}
                  setDropTarget={h.setDropTarget}
                  setAbsModal={h.setAbsModal}
                  formatHeader={formatHeader}
                  getWeatherIcon={getWeatherIcon}
                />
                {h.showAddShift && (
                  <AddShiftModal
                    open={h.showAddShift}
                    onClose={() => {
                      h.setShowAddShift(false);
                      h.setShiftData({
                        start_time_date: '',
                        start_time_time: '09:00',
                        end_time_time: '17:00',
                        employees_needed: 1,
                        work_area: '',
                        notes: '',
                        weekdays: [],
                        interval: 1,
                        repeat_until: '',
                        never_ends: false,
                        crm_task: null,
                        crm_task_title: '',
                      });
                    }}
                    onSaved={h.fetchTableShifts}
                    workAreaOptions={h.workAreaOptions}
                    initialData={h.shiftData}
                    isManager={h.isManager}
                    workAreaIds={h.workAreaIds}
                    onDefaultsChange={h.setLastShiftDefaults}
                  />
                )}
                <ClearAllModal
                  open={h.showClearAllModal}
                  onClose={() => h.setShowClearAllModal(false)}
                  onConfirm={h.handleClearAllConfirm}
                />
                {h.showCalendar && (
                  <div
                    className={`glass-modal-backdrop ${styles.calendarBackdrop}`}
                    onClick={() => h.setShowCalendar(false)}
                  >
                    <div
                      className={`glass-modal ${styles.calendarModal}`}
                      style={{
                        position: 'absolute',
                        top: h.calendarPos.top,
                        left: h.calendarPos.left,
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        onClick={() => h.setShowCalendar(false)}
                        className={styles.calendarCloseBtn}
                        aria-label="Close calendar"
                        title="Close"
                      >
                        &times;
                      </button>
                      <div className={styles.calendarTodayWrap}>
                        <button
                          onClick={() => {
                            h.setStartDate(dateUtil.getMonday(new Date()));
                            h.setShowCalendar(false);
                          }}
                          className={`glass-btn glass-btn-secondary ${styles.calendarTodayBtn}`}
                          aria-label="Zur\u00fcck zu heute"
                          title="Zur\u00fcck zu heute"
                        >
                          Heute
                        </button>
                      </div>
                      <DatePicker
                        selected={h.startDate}
                        onChange={(date: any) => {
                          h.setStartDate(dateUtil.getMonday(date as any));
                          h.setShowCalendar(false);
                        }}
                        selectsMultiple={null}
                        inline
                        locale="de"
                        calendarStartDay={1}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            {h.activeTab === 'timeTrackings' && (
              <TimeTrackings
                employees={h.allEmployees}
                activeEmployeeId={h.activeEmployeeId}
                onRangeChange={h.handleTTsRangeChange}
                workAreas={h.workAreaOptions}
                selectedWAs={h.ttSelectedWAs}
                onSelectedWAsChange={h.setTtSelectedWAs}
                renderHeaderControls={false}
                exportOpen={h.ttExportOpen}
                onExportOpenChange={h.setTtExportOpen}
                importOpen={h.ttImportOpen}
                onImportOpenChange={h.setTtImportOpen}
                addModalOpen={h.ttAddOpen}
                onAddModalOpenChange={h.setTtAddOpen}
                initialMonth={h.ttInitialMonth}
                initialRange={h.ttInitialRange}
              />
            )}
            {h.activeTab === 'absences' && (
              <Absences
                activeEmployeeId={h.activeEmployeeId}
                setActiveEmployeeId={h.setActiveEmployeeId}
                workAreas={h.workAreaOptions}
                selectedWAs={h.absenceSelectedWAs}
                onSelectedWAsChange={h.setAbsenceSelectedWAs}
                renderHeaderControls={false}
                addModalOpen={h.absenceAddOpen}
                onAddModalOpenChange={h.setAbsenceAddOpen}
                importModalOpen={h.absenceImportOpen}
                onImportModalOpenChange={h.setAbsenceImportOpen}
              />
            )}
            {h.activeTab === 'availabilities' && (
              <Availabilities
                employees={h.allEmployees}
                activeEmployeeId={h.activeEmployeeId}
                setActiveEmployeeId={h.setActiveEmployeeId}
              />
            )}
            {h.activeTab === 'dashboard' && (
              <ShiftDashboard allEmployees={h.allEmployees} />
            )}
          </div>
        </div>
      </div>

      <EditShiftModal
        open={h.showEditShift}
        onClose={h.closeEditShiftModal}
        editData={h.editShiftData}
        onEditDataChange={h.setEditShiftData}
        onSave={h.handleEditShiftSave}
        onDelete={handleDeleteWithUndo}
        workAreaOptions={h.workAreaOptions}
        addOneHour={addOneHour}
      />
      <WeeklyShiftsAbsenceModal
        absModal={h.absModal}
        setAbsModal={h.setAbsModal}
        weekAbsences={h.weekAbsences}
        allEmployees={h.allEmployees}
      />
      <AutoAssignOverlay
        showConfirm={h.showAutoAssignConfirm}
        onConfirm={h.executeAutoAssign}
        onCancelConfirm={() => h.setShowAutoAssignConfirm(false)}
        isAssigning={h.isAssigning}
        assignmentLogs={h.assignmentLogs}
        onCloseOverlay={() => h.setIsAssigning(false)}
      />
    </div>
  );
}

export default WeeklyShifts;
